import { useMemo } from "react";
import { parseISO } from "date-fns";
import { OverlayEvent } from "./overlay.types";
import { calculateDailyHoursMap, countEventsInRange } from "./hoursMetrics";
import { CalendarDayHeader } from "./CalendarDayHeader";
import { CalendarToolbar } from "./CalendarToolbar";
import { overlayEventPropGetter } from "./overlayEventStyle";

type DateRangeFilter = {
    start_date: string;
    end_date: string;
};

type UseOverlayCalendarBindingsArgs = {
    calendarEvents: Array<any>;
    backgroundEvents: Array<OverlayEvent> | undefined;
    showBackgroundEvents: boolean | undefined;
    discountBreaks: boolean | undefined;
    dateRange: DateRangeFilter | undefined;
};

type EventPropGetterResult = { style?: any; className?: string };
type EventPropGetter = (event: any) => EventPropGetterResult;

export const useOverlayCalendarBindings = ({
    calendarEvents,
    backgroundEvents,
    showBackgroundEvents,
    discountBreaks,
    dateRange,
}: UseOverlayCalendarBindingsArgs) => {
    const effectiveBackgroundEvents = showBackgroundEvents ? backgroundEvents : undefined;

    const dateRangeStart = dateRange?.start_date;
    const dateRangeEnd = dateRange?.end_date;
    const visibleRange = useMemo(() => {
        if (!dateRangeStart || !dateRangeEnd) return undefined;
        const bounds = { start: parseISO(dateRangeStart), end: parseISO(dateRangeEnd) };
        return bounds;
    }, [dateRangeStart, dateRangeEnd]);

    const hoursMap = useMemo(
        () => calculateDailyHoursMap(calendarEvents, effectiveBackgroundEvents, !!discountBreaks, visibleRange),
        [calendarEvents, effectiveBackgroundEvents, discountBreaks, visibleRange]
    );

    const visibleEventsCount = useMemo(
        () => countEventsInRange(calendarEvents, visibleRange),
        [calendarEvents, visibleRange]
    );

    const dayHeaderRenderer = useMemo(() => {
        const renderer = ({ date, label }: { date: Date; label: string }) => (
            <CalendarDayHeader
                date={date}
                label={label}
                hoursMap={hoursMap}
                showMetric={!!showBackgroundEvents}
            />
        );
        return renderer;
    }, [hoursMap, showBackgroundEvents]);

    const toolbarRenderer = useMemo(() => {
        const renderer = (toolbarProps: any) => (
            <CalendarToolbar
                label={toolbarProps.label}
                view={toolbarProps.view}
                views={toolbarProps.views}
                onNavigate={toolbarProps.onNavigate}
                onView={toolbarProps.onView}
                hoursMap={hoursMap}
                showTotal={!!showBackgroundEvents}
                visibleEventsCount={visibleEventsCount}
            />
        );
        return renderer;
    }, [hoursMap, showBackgroundEvents, visibleEventsCount]);

    const composeEventPropGetter = (baseGetter: EventPropGetter): EventPropGetter => {
        const composed = (event: any) => {
            if (event?.overlayType) {
                const overlayStyle = overlayEventPropGetter(event);
                return overlayStyle;
            }
            const baseResult = baseGetter(event);
            return baseResult;
        };
        return composed;
    };

    return {
        effectiveBackgroundEvents,
        dayHeaderRenderer,
        toolbarRenderer,
        composeEventPropGetter,
    };
};
